/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 */
import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, useIntl} from 'react-intl';

import {useProducts} from '@amblique/king-living/app/hooks/use-products';
import Link from '@salesforce/retail-react-app/app/components/link';
import {
  Box,
  Button,
  Flex,
  Image,
  Skeleton,
  Stack,
  Text,
  VStack,
  Wrap,
  WrapItem,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useBlogsPagesByTags} from '@amblique/king-living/app/contentful/hooks';
import {
  SEARCH_SPOTLIGHT_BLOG_LIMIT,
  getIdleBlogImageUrl,
  mapPageToSpotlightBlog,
} from '@amblique/king-living/app/components/search/search-spotlight-blog-helpers';
import TrendingProductRow from '@amblique/king-living/app/components/search/partials/trending-product-row';
import {ensureArray} from '@amblique/king-living/app/utils/utils';

const SEARCH_IDLE_TRENDING_PRODUCT_MAX = 3;

/**
 * Header search idle: popular terms, trending products, spotlight pages from `articleTags`.
 */
const SearchIdlePanel = ({
  isHeaderLoading,
  searchIdle,
  onPickPopularSearch,
  onPickBlog,
  onPickProduct,
  variant,
}) => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('SearchIdlePanel', {variant});
  const popularTerms = useMemo(() => {
    return ensureArray(searchIdle?.popularSearches)
      .map((t) => String(t).trim())
      .filter(Boolean);
  }, [searchIdle?.popularSearches]);

  const articleTagIds = useMemo(() => {
    return ensureArray(searchIdle?.articleTags)
      .map((t) => String(t).trim())
      .filter(Boolean);
  }, [searchIdle?.articleTags]);

  const {data: pagesCollection, isLoading: isBlogsByTagsLoading} = useBlogsPagesByTags(
    articleTagIds,
    {
      limit: SEARCH_SPOTLIGHT_BLOG_LIMIT,
      skip: 0,
      useRawArticleTagIds: true,
      queryOptions: {
        enabled: articleTagIds.length > 0,
      },
    }
  );

  const spotlightBlogs = useMemo(() => {
    const items = pagesCollection?.items;

    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }

    return items.map(mapPageToSpotlightBlog).filter(Boolean).slice(0, SEARCH_SPOTLIGHT_BLOG_LIMIT);
  }, [pagesCollection?.items]);

  const isSpotlightLoading = articleTagIds.length > 0 && isBlogsByTagsLoading;

  const showSpotlightColumn =
    articleTagIds.length > 0 &&
    (isSpotlightLoading || spotlightBlogs.length > 0);

  const idList = useMemo(() => {
    return ensureArray(searchIdle?.trendingProductIds)
      .map((id) => String(id).trim())
      .filter(Boolean)
      .slice(0, SEARCH_IDLE_TRENDING_PRODUCT_MAX);
  }, [searchIdle?.trendingProductIds]);

  const idsCsv = useMemo(() => idList.join(','), [idList]);

  const trendingProductParameters = useMemo(
    () => ({
      ids: idsCsv,
      allImages: true,
      perPricebook: true,
    }),
    [idsCsv]
  );

  const {
    data: productsResult,
    isError: isTrendingProductsError,
    isFetching: isTrendingProductsFetching,
    isLoading: isTrendingProductsLoading,
  } = useProducts(
    {
      parameters: trendingProductParameters,
    },
    {
      enabled: idsCsv.length > 0,
    }
  );

  const isTrendingProductsPending =
    idsCsv.length > 0 &&
    !isTrendingProductsError &&
    (isTrendingProductsLoading || isTrendingProductsFetching);

  const trendingOrdered = useMemo(() => {
    const payload = productsResult?.data;
    const rows = Array.isArray(payload) ? payload : payload?.data;

    if (!Array.isArray(rows) || !idList.length) {
      return [];
    }

    const map = new Map(rows.map((p) => [p.id ?? p.productId, p]));

    return idList.map((id) => map.get(id)).filter(Boolean);
  }, [productsResult?.data, idList]);

  const productTypeLabel = intl.formatMessage({
    id: 'search.idle.product_tag',
    defaultMessage: 'Product',
  });

  if (isHeaderLoading) {
    return (
      <Box data-testid="search-idle-loading" {...styles.root} width="100%">
        <Flex
          {...styles.layoutRow}
          width="full"
        >
          <Box {...styles.mainColumn}>
            <Skeleton height="10px" mb={3} width="160px" />
            <Wrap spacing={2} {...styles.popularTags}>
              {[0, 1, 2, 3, 4].map((i) => (
                <WrapItem key={i}>
                  <Skeleton borderRadius="100px" height="38px" width="88px" />
                </WrapItem>
              ))}
            </Wrap>
            <Skeleton height="10px" mb={3} mt={12} width="180px" />
            <VStack align="stretch" spacing={1}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} height={{base: '80px', md: '75px'}} width="full" />
              ))}
            </VStack>
          </Box>
          <Box {...styles.spotlightColumn}>
            <Box {...styles.spotlightList}>
              <Box {...styles.spotlightFirstContainer}>
                <Box {...styles.spotlightHeadingBlock}>
                  <Skeleton height="10px" width="100px" />
                  <Skeleton height="176px" width="full" />
                </Box>
                <Skeleton height="16px" width="92%" />
              </Box>
              <Box {...styles.spotlightItem}>
                <Skeleton height="176px" width="full" />
                <Skeleton height="16px" width="85%" />
              </Box>
            </Box>
          </Box>
        </Flex>
      </Box>
    );
  }

  if (!searchIdle) {
    return null;
  }

  const hasContent =
    popularTerms.length > 0 || articleTagIds.length > 0 || idList.length > 0;

  if (!hasContent) {
    return null;
  }

  const showTrending = idList.length > 0;
  const isSearchResultsPage = variant === 'searchResults';
  const mainSectionsSpacing = popularTerms.length > 0 && showTrending ? 12 : 0;

  const spotlightColumn = showSpotlightColumn ? (
    <Box {...styles.spotlightColumn}>
      {isSpotlightLoading && spotlightBlogs.length === 0 ? (
        <Box {...styles.spotlightList}>
          <Box {...styles.spotlightFirstContainer}>
            <Box {...styles.spotlightHeadingBlock}>
              <Skeleton height="10px" width="100px" />
              <Skeleton height="176px" width="full" />
            </Box>
            <Skeleton height="16px" width="92%" />
          </Box>
          <Box {...styles.spotlightItem}>
            <Skeleton height="176px" width="full" />
            <Skeleton height="16px" width="85%" />
          </Box>
        </Box>
      ) : null}
      {spotlightBlogs.length > 0 ? (
        <Box {...styles.spotlightList}>
          {spotlightBlogs.map((blog, index) => {
            const name = blog?.title || blog?.pageTitle || '';
            const src = getIdleBlogImageUrl(blog);
            const pageSlug = String(blog?.pageSlug || '').trim();
            const isFirst = index === 0;

            if (!pageSlug) {
              return null;
            }

            const imageNode = (
              <Box {...styles.spotlightImage}>
                {src ? (
                  <Image
                    alt={name || ''}
                    h="full"
                    minH="inherit"
                    objectFit="cover"
                    src={src}
                    w="full"
                  />
                ) : (
                  <Box bg="blackAlpha.50" h="full" minH="inherit" w="full" />
                )}
              </Box>
            );

            return (
              <Link
                key={blog?.pageSysId || blog?.pageSlug || blog?.sys?.id || `spotlight-${index}`}
                aria-label={name || 'Spotlight article'}
                to={pageSlug}
                {...styles.spotlightCard}
                onClick={() => {
                  onPickBlog(blog, {skipNavigate: true});
                }}
              >
                {isFirst ? (
                  <Box {...styles.spotlightFirstContainer}>
                    <Box {...styles.spotlightHeadingBlock}>
                      <Text
                        data-testid={
                          isSearchResultsPage ? 'search-section-eyebrow' : undefined
                        }
                        {...styles.spotlightHeading}
                      >
                        <FormattedMessage
                          defaultMessage="Spotlight"
                          id="search.idle.spotlight_heading"
                        />
                      </Text>
                      {imageNode}
                    </Box>
                    <Text
                      data-testid={
                        isSearchResultsPage ? 'search-spotlight-article-title' : undefined
                      }
                      {...styles.spotlightName}
                      noOfLines={4}
                    >
                      {name}
                    </Text>
                  </Box>
                ) : (
                  <Box {...styles.spotlightItem}>
                    {imageNode}
                    <Text
                      data-testid={
                        isSearchResultsPage ? 'search-spotlight-article-title' : undefined
                      }
                      {...styles.spotlightName}
                      noOfLines={4}
                    >
                      {name}
                    </Text>
                  </Box>
                )}
              </Link>
            );
          })}
        </Box>
      ) : null}
    </Box>
  ) : null;

  const mainColumn = (
    <Stack align="stretch" spacing={mainSectionsSpacing} width="full">
      {popularTerms.length > 0 && (
        <Box {...styles.popularSection}>
          <Text
            data-testid={isSearchResultsPage ? 'search-section-eyebrow' : undefined}
            {...styles.popularHeading}
          >
            <FormattedMessage
              defaultMessage="Popular searches"
              id="search.idle.popular_heading"
            />
          </Text>
          <Wrap gap={isSearchResultsPage ? '8px' : undefined} {...styles.popularTags}>
            {popularTerms.map((term) => (
              <WrapItem key={term}>
                <Button
                  variant="unstyled"
                  {...styles.popularTag}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onPickPopularSearch(term);
                  }}
                >
                  {term}
                </Button>
              </WrapItem>
            ))}
          </Wrap>
        </Box>
      )}

      {showTrending && (
        <Box {...styles.trendingSection}>
          <Text {...styles.trendingHeading}>
            <FormattedMessage
              defaultMessage="Trending products"
              id="search.idle.trending_heading"
            />
          </Text>
          {isTrendingProductsPending ? (
            isSearchResultsPage ? (
              <>
                <Box data-search-trending-list-mobile {...styles.trendingListMobile}>
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} height="80px" width="full" />
                  ))}
                </Box>
                <Box data-search-trending-list-grid {...styles.trendingListGrid}>
                  {[0, 1, 2].map((i) => (
                    <Box key={i} width="full">
                      <Skeleton aspectRatio={1} width="full" />
                      <Skeleton height="16px" mt={3} width="85%" />
                      <Skeleton height="12px" mt={2} width="55%" />
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Box {...styles.trendingList}>
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} height={{base: '80px', md: '75px'}} width="full" />
                ))}
              </Box>
            )
          ) : isSearchResultsPage ? (
            <>
              <Box data-search-trending-list-mobile {...styles.trendingListMobile}>
                {trendingOrdered.map((product, index) => (
                  <TrendingProductRow
                    key={product?.id ?? product?.productId ?? `trend-${index}`}
                    layout="list"
                    product={product}
                    productTypeLabel={productTypeLabel}
                    styles={styles}
                    onPickProduct={(pickedProduct) => onPickProduct(pickedProduct, index)}
                  />
                ))}
              </Box>
              <Box data-search-trending-list-grid {...styles.trendingListGrid}>
                {trendingOrdered.map((product, index) => (
                  <TrendingProductRow
                    key={product?.id ?? product?.productId ?? `trend-grid-${index}`}
                    layout="grid"
                    product={product}
                    productTypeLabel={productTypeLabel}
                    styles={styles}
                    onPickProduct={(pickedProduct) => onPickProduct(pickedProduct, index)}
                  />
                ))}
              </Box>
            </>
          ) : (
            <Box {...styles.trendingList}>
              {trendingOrdered.map((product, index) => (
                <TrendingProductRow
                  key={product?.id ?? product?.productId ?? `trend-${index}`}
                  layout="list"
                  product={product}
                  productTypeLabel={productTypeLabel}
                  styles={styles}
                  onPickProduct={(pickedProduct) => onPickProduct(pickedProduct, index)}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Stack>
  );

  return (
    <Box
      data-testid="search-idle-panel"
      {...styles.root}
      className={isSearchResultsPage ? 'search-idle-panel--search-results' : undefined}
      width="100%"
    >
      {showSpotlightColumn ? (
        <Flex {...styles.layoutRow}>
          <Box {...styles.mainColumn}>
            {mainColumn}
          </Box>
          {spotlightColumn}
        </Flex>
      ) : (
        <Box {...styles.mainColumn}>{mainColumn}</Box>
      )}
    </Box>
  );
};

SearchIdlePanel.propTypes = {
  isHeaderLoading: PropTypes.bool.isRequired,
  variant: PropTypes.oneOf(['searchResults']),
  searchIdle: PropTypes.shape({
    popularSearches: PropTypes.array,
    trendingProductIds: PropTypes.array,
    title: PropTypes.string,
    articleTags: PropTypes.array,
  }),
  onPickPopularSearch: PropTypes.func.isRequired,
  onPickBlog: PropTypes.func.isRequired,
  onPickProduct: PropTypes.func.isRequired,
};

export default SearchIdlePanel;
